import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useTheme } from '../context/ThemeContext';
import './AddChildModal.css';

// Icons
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const UserPlusIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const AddChildModal = ({ isOpen, onClose, parentId, onChildAdded }) => {
  const { theme, isDark } = useTheme();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [canTrade, setCanTrade] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!firstName.trim() || !username.trim()) {
      setError('First name and username are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Check if username is taken
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('username', username.trim().toLowerCase())
        .single();

      if (existingUser) {
        setError('Username is already taken');
        setSaving(false);
        return;
      }

      // Check if email is already taken (if provided)
      if (email.trim()) {
        const { data: existingEmail } = await supabase
          .from('users')
          .select('id, auth_id')
          .eq('email', email.trim().toLowerCase())
          .single();

        if (existingEmail) {
          if (existingEmail.auth_id) {
            setError('This email is already registered to an account. They can link to you from their profile instead.');
          } else {
            setError('This email is already in use by another profile');
          }
          setSaving(false);
          return;
        }
      }

      // Create child profile (no auth_id since parent manages it)
      const { data: childData, error: childError } = await supabase
        .from('users')
        .insert({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          username: username.trim().toLowerCase(),
          email: email.trim() || null,
          is_under_18: true,
          wants_to_trade: true,
          has_own_auth: false,
          intake_completed: true, // Skip onboarding for parent-created profiles
        })
        .select()
        .single();

      if (childError) throw childError;

      // Create family link
      const { error: linkError } = await supabase
        .from('family_links')
        .insert({
          parent_id: parentId,
          child_id: childData.id,
          can_trade: canTrade,
        });

      if (linkError) throw linkError;

      // Reset form and close
      setFirstName('');
      setLastName('');
      setUsername('');
      setEmail('');
      setCanTrade(false);

      if (onChildAdded) {
        onChildAdded(childData);
      }

      onClose();
    } catch (err) {
      console.error('Error adding child:', err);
      setError(err.message || 'Failed to add child. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFirstName('');
    setLastName('');
    setUsername('');
    setEmail('');
    setCanTrade(false);
    setError(null);
    onClose();
  };

  return (
    <div
      className="add-child-modal-overlay"
      onClick={handleClose}
      style={{ background: theme.overlay }}
    >
      <div
        className="add-child-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ background: theme.bgCard }}
      >
        <button
          className="add-child-modal-close"
          onClick={handleClose}
          style={{ color: theme.textSecondary }}
        >
          <XIcon />
        </button>

        <div className="add-child-modal-icon">
          <UserPlusIcon />
        </div>

        <h2 className="add-child-modal-title" style={{ color: theme.text }}>
          Add Child Profile
        </h2>
        <p className="add-child-modal-subtitle" style={{ color: theme.textMuted }}>
          Create a profile for your child. You can manage their account from your dashboard.
        </p>

        <div className="add-child-form">
          <div className="add-child-row">
            <div className="add-child-field">
              <label className="add-child-label" style={{ color: theme.textMuted }}>
                First Name *
              </label>
              <input
                type="text"
                className="add-child-input"
                placeholder="Enter name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  color: theme.text
                }}
              />
            </div>
            <div className="add-child-field">
              <label className="add-child-label" style={{ color: theme.textMuted }}>
                Last Name
              </label>
              <input
                type="text"
                className="add-child-input"
                placeholder="Optional"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  color: theme.text
                }}
              />
            </div>
          </div>

          <div className="add-child-field">
            <label className="add-child-label" style={{ color: theme.textMuted }}>
              Username *
            </label>
            <div
              className="add-child-input-prefix"
              style={{
                background: theme.bg,
                border: `1px solid ${theme.border}`
              }}
            >
              <span className="add-child-prefix" style={{ color: theme.textSecondary }}>
                @
              </span>
              <input
                type="text"
                className="add-child-input with-prefix"
                placeholder="username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                style={{
                  background: theme.bg,
                  color: theme.text
                }}
              />
            </div>
          </div>

          <div className="add-child-field">
            <label className="add-child-label" style={{ color: theme.textMuted }}>
              Email (optional)
            </label>
            <input
              type="email"
              className="add-child-input"
              placeholder="For future independent login"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                background: theme.bg,
                border: `1px solid ${theme.border}`,
                color: theme.text
              }}
            />
            <p className="add-child-hint" style={{ color: theme.textSecondary }}>
              If provided, they can set up their own login later
            </p>
          </div>

          <div
            className="add-child-toggle-row"
            style={{
              background: theme.bg,
              border: `1px solid ${theme.border}`
            }}
          >
            <div className="add-child-toggle-info">
              <span className="add-child-toggle-label" style={{ color: theme.text }}>
                Allow Trading
              </span>
              <span className="add-child-toggle-desc" style={{ color: theme.textSecondary }}>
                Can send/receive trade requests and chat with traders
              </span>
            </div>
            <button
              className={`add-child-toggle ${canTrade ? 'active' : ''}`}
              onClick={() => setCanTrade(!canTrade)}
              style={{ background: canTrade ? '#22c55e' : theme.border }}
            >
              <div className="add-child-toggle-knob" />
            </button>
          </div>

          {error && (
            <div className="add-child-error">{error}</div>
          )}

          <div className="add-child-actions">
            <button
              className="add-child-btn secondary"
              onClick={handleClose}
              style={{
                border: `1px solid ${theme.border}`,
                color: theme.textMuted
              }}
            >
              Cancel
            </button>
            <button
              className="add-child-btn primary"
              onClick={handleSubmit}
              disabled={saving || !firstName.trim() || !username.trim()}
            >
              {saving ? 'Adding...' : 'Add Child'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddChildModal;
